import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Component } from '@/types';

export interface ModalConfig {
  enabled: boolean;
  triggerText: string;
  title: string;
  size: string;
  triggerVariant?: string;
  showBackdrop?: boolean;
  closeOnBackdropClick?: boolean;
  showCloseButton?: boolean;
}

export const MODAL_CLOSE_EVENT = 'terramatrix:close-modal';

const SIZE_MAP: Record<string, string> = {
  sm: '400px',
  md: '600px',
  lg: '800px',
  full: '100%',
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const getStringValue = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const getComponentTitle = (component: Component): string =>
  getStringValue(component.properties?.title) ?? component.name ?? 'Modal';

const isEnabledModalConfig = (value: unknown): value is Record<string, unknown> =>
  isRecord(value) && value.enabled === true;

function getTriggerButtonStyle(triggerVariant: string): React.CSSProperties {
  const primary = 'var(--color-primary, #4CAF50)';
  const secondary = 'var(--color-secondary, #2196F3)';

  return {
    padding: '8px 20px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    border: triggerVariant === 'outline' || triggerVariant === 'ghost'
      ? `1px solid ${primary}`
      : '1px solid transparent',
    backgroundColor: triggerVariant === 'primary' ? primary
      : triggerVariant === 'secondary' ? secondary
      : 'transparent',
    color: triggerVariant === 'primary' || triggerVariant === 'secondary'
      ? '#ffffff'
      : primary,
  };
}

/**
 * Extracts and normalizes modal config from a component's properties.modal.
 * Returns null if the component is not a modal container.
 */
export function getModalConfig(component: Component): ModalConfig | null {
  const modal = component.properties?.modal;
  if (!modal) return null;
  if (modal === true) {
    return {
      enabled: true,
      triggerText: 'Open',
      title: getComponentTitle(component),
      size: 'md',
    };
  }
  if (isEnabledModalConfig(modal)) {
    return {
      enabled: true,
      triggerText: getStringValue(modal.triggerText) ?? 'Open',
      title: getStringValue(modal.title) ?? getComponentTitle(component),
      size: getStringValue(modal.size) ?? 'md',
      triggerVariant: getStringValue(modal.triggerVariant),
      showBackdrop: modal.showBackdrop !== false,
      closeOnBackdropClick: modal.closeOnBackdropClick !== false,
      showCloseButton: modal.showCloseButton !== false,
    };
  }
  return null;
}

interface ContainerModalWrapperProps {
  component: Component;
  modalConfig: ModalConfig;
  children: React.ReactNode;
}

/**
 * Renders a container as a modal in viewer mode:
 * - Shows a trigger button on the page
 * - On click, opens a fixed-position overlay with the container's children
 * - Includes close button, backdrop click, and Escape key handling
 */
export const ContainerModalWrapper: React.FC<ContainerModalWrapperProps> = ({
  component,
  modalConfig,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleClose = (event: Event) => {
      if (!(event instanceof CustomEvent) || !isRecord(event.detail)) return;
      if (event.detail.componentId === component.id) {
        setIsOpen(false);
      }
    };

    window.addEventListener(MODAL_CLOSE_EVENT, handleClose);
    return () => {
      window.removeEventListener(MODAL_CLOSE_EVENT, handleClose);
    };
  }, [component.id]);

  const {
    triggerText,
    title,
    size,
    triggerVariant = 'primary',
    showBackdrop = true,
    closeOnBackdropClick = true,
    showCloseButton = true,
  } = modalConfig;

  const maxWidth = SIZE_MAP[size] || SIZE_MAP.md;

  return (
    <>
      <button
        data-testid="modal-trigger"
        onClick={() => setIsOpen(true)}
        style={getTriggerButtonStyle(triggerVariant)}
      >
        {triggerText}
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          data-testid="modal-overlay"
          onClick={closeOnBackdropClick ? () => setIsOpen(false) : undefined}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: showBackdrop ? 'rgba(0, 0, 0, 0.5)' : 'transparent',
          }}
        >
          <div
            ref={dialogRef}
            data-testid="modal-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`modal-title-${component.id}`}
            tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') setIsOpen(false); }}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth,
              width: size === 'full' ? '100%' : '90%',
              maxHeight: '90vh',
              backgroundColor: '#ffffff',
              borderRadius: size === 'full' ? '0' : '12px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 20px',
                borderBottom: '1px solid #e5e7eb',
              }}
            >
              <h2
                id={`modal-title-${component.id}`}
                style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#111827' }}
              >
                {title}
              </h2>
              {showCloseButton && (
                <button
                  data-testid="modal-close"
                  aria-label="Close modal"
                  onClick={() => setIsOpen(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#6b7280',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Body — renders the container's children */}
            <div
              style={{
                padding: '20px',
                overflowY: 'auto',
                flex: 1,
              }}
            >
              {children}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

/**
 * Preview overlay for modals in the editor — used by ModalPreviewButton.
 * Renders the modal in viewer mode with a "Preview Mode" banner.
 */
export const ModalPreviewOverlay: React.FC<{
  modalConfig: ModalConfig;
  componentId: string;
  children: React.ReactNode;
  onClose: () => void;
}> = ({ modalConfig, componentId, children, onClose }) => {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
  }, []);

  const { title, size, showCloseButton = true } = modalConfig;
  const maxWidth = SIZE_MAP[size] || SIZE_MAP.md;

  return ReactDOM.createPortal(
    <div
      data-testid="modal-preview-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10002,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'hsl(var(--surface-overlay, 0 0% 0% / 0.7))',
      }}
    >
      {/* Preview Mode Banner */}
      <div
        style={{
          position: 'absolute',
          top: '12px',
          left: '50%',
          transform: 'translateX(-50%)',
          backgroundColor: 'hsl(var(--popover))',
          color: 'hsl(var(--popover-foreground))',
          padding: '6px 16px',
          borderRadius: '20px',
          fontSize: '12px',
          fontWeight: 500,
          border: '1px solid hsl(var(--border))',
          boxShadow: 'var(--shadow-md)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          zIndex: 10000,
        }}
      >
        <span>Preview Mode</span>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'hsl(var(--muted-foreground))',
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: 1,
            padding: '0 2px',
          }}
          aria-label="Close preview"
        >
          ✕
        </button>
      </div>

      {/* Modal Dialog */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={`modal-preview-title-${componentId}`}
        tabIndex={-1}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth,
          width: size === 'full' ? '100%' : '90%',
          maxHeight: '80vh',
          backgroundColor: 'hsl(var(--background))',
          color: 'hsl(var(--foreground))',
          border: '1px solid hsl(var(--border))',
          borderRadius: size === 'full' ? '0' : '12px',
          boxShadow: 'var(--shadow-xl)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid hsl(var(--border))',
            backgroundColor: 'hsl(var(--card))',
          }}
        >
          <h2
            id={`modal-preview-title-${componentId}`}
            style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 600,
              color: 'hsl(var(--card-foreground))',
            }}
          >
            {title}
          </h2>
          {showCloseButton && (
            <button
              aria-label="Close modal"
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: 'hsl(var(--muted-foreground))',
                padding: '4px 8px',
                borderRadius: '4px',
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          )}
        </div>

        {/* Body */}
        <div
          style={{
            padding: '20px',
            overflowY: 'auto',
            flex: 1,
            backgroundColor: 'hsl(var(--background))',
          }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default ContainerModalWrapper;
